import { z } from "zod";

// ─── Phone ───────────────────────────────────────────────
export const phoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{6,14}$/, "E.164 format required (e.g. +905551234567)");

// ─── Auth ────────────────────────────────────────────────
export const requestCodeSchema = z.object({
  phone: phoneSchema,
  turnstileToken: z.string().min(1, "Turnstile token required"),
});

export const verifyCodeSchema = z.object({
  phone: phoneSchema,
  code: z.string().length(6, "Code must be 6 digits").regex(/^\d{6}$/),
  verificationSid: z.string().optional(),
});

/**
 * Misafir adı — self-register PUBLIC bir linkten geldiği için bu değer doğrudan
 * çiftin misafir listesine ve SMS gönderim ekranına düşer. Kurallar:
 * girişte normalize (trim + çoklu boşluk tekle), en az 2 harf, harf/boşluk ve
 * `' - .` dışında karakter yok (rakam, `@`, `/` → red), alan adı görünümlü
 * diziler ("bit.ly") reddedilir. Parse edilmiş değer NORMALİZE olarak döner —
 * çağıran taraf `parse()` sonucunu kullanmalı, ham girdiyi değil.
 */
export const guestNameSchema = z
  .string()
  .max(200, "Name too long") // transform öncesi kaba tavan (DoS koruması)
  .transform((s) => s.trim().replace(/\s+/g, " "))
  .pipe(
    z
      .string()
      .min(2, "Name too short")
      .max(80, "Name too long")
      .regex(/^[\p{L}\p{M}][\p{L}\p{M} '’.-]*$/u, "Name contains invalid characters")
      .refine(
        (s) => (s.match(/\p{L}/gu) ?? []).length >= 2,
        "Name must contain at least 2 letters",
      )
      // "bit.ly", "site.com" gibi alan adı kalıpları — "Ahmet A." geçerli kalır.
      .refine((s) => !/\p{L}\.\p{L}{2,}/u.test(s), "Name must not contain a link"),
  );

export const qrRegisterSchema = z.object({
  name: guestNameSchema,
  phone: phoneSchema,
  turnstileToken: z.string().min(1, "Turnstile token required"),
  // KVKK: misafir davet/hatırlatma SMS'lerine açık rıza (default işaretsiz).
  smsConsent: z.boolean().optional(),
});

// ─── Landing: teklif / iletişim formu ────────────────────
// Fiyat listesi yok (karar: 2026-10-12) — ziyaretçi formu doldurur, worker
// contact@mertbal.com'a hazır şablonlu e-posta atar. Alan kümesi web formu,
// worker doğrulaması ve mailto yedeği için TEK kaynak.
export const CONTACT_EVENT_TYPES = [
  "wedding",
  "wedding_henna",
  "henna",
  "engagement",
  "promise",
  "other",
] as const;
export type ContactEventType = (typeof CONTACT_EVENT_TYPES)[number];

export const CONTACT_GUEST_RANGES = ["lt50", "50_150", "150_300", "300_500", "gt500"] as const;
export type ContactGuestRange = (typeof CONTACT_GUEST_RANGES)[number];

/**
 * Kademe sınırlarının varsayılanı — gerçek değer admin "Fiyatlar"dan
 * (`site:pricing.bounds`, 2026-10-15). Anahtarlar (`lt50`…`gt500`) DB'de
 * saklanan SABİT kimliklerdir (CHECK kısıtı); sayılar sınırlardan okunur:
 * 1. kademe → b1'e kadar, 2. → b1–b2, 3. → b2–b3, 4. → b3–b4, 5. → b4+.
 */
export const DEFAULT_GUEST_BOUNDS: readonly number[] = [50, 150, 300, 500];

const validBounds = (b: readonly number[] | null | undefined): readonly number[] =>
  Array.isArray(b) && b.length === CONTACT_GUEST_RANGES.length - 1 && b.every((v, i) => Number.isInteger(v) && v > 0 && (i === 0 || v > b[i - 1]))
    ? b
    : DEFAULT_GUEST_BOUNDS;

/**
 * Aralığın misafir üst sınırı; son kademe açık uçlu (`null`). Satılan hakkın
 * misafir sayısı TAM OLARAK bu üst sınırdır ("aralıksa üst limit").
 */
export function guestRangeMax(bounds: readonly number[] | null | undefined, range: ContactGuestRange): number | null {
  const i = CONTACT_GUEST_RANGES.indexOf(range);
  const b = validBounds(bounds);
  return i < b.length ? b[i] : null;
}

/** n misafiri karşılayan en küçük kademe; son sınırın üstü → açık uçlu son kademe. */
export function guestRangeFor(bounds: readonly number[] | null | undefined, n: number): ContactGuestRange {
  const b = validBounds(bounds);
  const i = b.findIndex((v) => n <= v);
  return CONTACT_GUEST_RANGES[i === -1 ? b.length : i];
}

/** Aralığın alt sınırı — "500'den fazla" gibi açık uçlu ifade ve "150–300" etiketi için. */
export function guestRangeMin(bounds: readonly number[] | null | undefined, range: ContactGuestRange): number {
  const i = CONTACT_GUEST_RANGES.indexOf(range);
  return i <= 0 ? 0 : validBounds(bounds)[i - 1];
}

/** YYYY-MM-DD; bugünden (dün tolerans: saat dilimi) en fazla 5 yıl sonrasına. */
const contactEventDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be YYYY-MM-DD")
  .refine((s) => {
    const t = Date.parse(`${s}T00:00:00Z`);
    if (Number.isNaN(t)) return false;
    const day = 86_400_000;
    const now = Date.now();
    return t >= now - 2 * day && t <= now + 5 * 366 * day;
  }, "Date out of range");

export const contactRequestSchema = z.object({
  name: guestNameSchema,
  email: z.string().trim().toLowerCase().max(254).email("Invalid email"),
  phone: phoneSchema,
  eventType: z.enum(CONTACT_EVENT_TYPES),
  eventDate: contactEventDateSchema,
  /**
   * ESKİ istemci: misafir aralığı. Yeni form (2026-10-16) misafir sayısını PAKET seçimiyle sorar
   * (`plan`: Free / Pro / Max — sayılar paket tanımından — ya da `custom` "en büyük paketten fazla");
   * aralık sunucuda paketten türetilir (yalnız eski sütun için).
   */
  guestRange: z.enum(CONTACT_GUEST_RANGES).optional(),
  city: z.string().trim().max(80).optional().default(""),
  message: z.string().trim().max(2000).optional().default(""),
  /** Seçilen paket (ya da plan kartından gelinen). `custom` → en büyük paketten fazla, özel teklif. */
  plan: z.enum(["free", "pro", "max", "custom"]).optional(),
  /** KVKK açık rıza — işaretsiz gönderilemez. */
  consent: z.literal(true),
  lang: z.enum(["tr", "en", "fr", "es"]).optional(),
  /** Honeypot: insanlar görmez, botlar doldurur → dolu ise sessizce yut. */
  website: z.string().max(200).optional().default(""),
  turnstileToken: z.string().min(1, "Turnstile token required"),
}).refine((v) => v.guestRange !== undefined || v.plan !== undefined, {
  message: "guest_choice_required",
  path: ["plan"],
});
export type ContactRequest = z.infer<typeof contactRequestSchema>;

export const registrationConfigSchema = z.object({
  allowSelfRegister: z.boolean().optional(),
  requireOtp: z.boolean().optional(),
  registrationOpensAt: z.string().datetime({ offset: true }).optional().nullable(),
  // Kayıt penceresinin kapanışı: bu andan sonra self-register reddedilir
  // (düğün bitince public QR linki süresiz açık kalmasın).
  registrationClosesAt: z.string().datetime({ offset: true }).optional().nullable(),
});

export const rsvpConfigSchema = z.object({
  showDietField:  z.boolean().optional(),
  showNotesField: z.boolean().optional(),
  maxHeadcount:   z.number().int().min(1).max(20).optional(),
});

// ─── RSVP ────────────────────────────────────────────────
export const rsvpStatusSchema = z.enum(["yes", "no", "maybe"]);

export const dietSchema = z.enum([
  "none",
  "vegan",
  "vegetarian",
  "gluten_free",
  "other",
]);

export const rsvpSchema = z.object({
  status: rsvpStatusSchema,
  headcount: z.number().int().min(1).max(10),
  diet: dietSchema.default("none"),
  notes: z.string().max(500).optional().default(""),
});

// ─── Media Upload ────────────────────────────────────────
export const mediaKindSchema = z.enum(["image", "video"]);

export const uploadRequestSchema = z.object({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1),
  kind: mediaKindSchema,
});

export const mediaCompleteSchema = z.object({
  mediaId: z.string().uuid(),
  r2Key: z.string().min(1),
});

export const mediaStatusSchema = z.enum(["pending", "approved", "rejected"]);

// ─── Wedding ─────────────────────────────────────────────
/**
 * Düğün slug'ı olamayacak yollar — web uygulamasının sabit sayfaları
 * (`/demo`, `/privacy`, `/terms`, `/checkout`), worker rotaları (`/api`, `/cdn`), `public/` klasörleri
 * ve organizatör panelinin üst düzey sayfaları (`/signup`, `/packages`). Yollar İngilizce (2026-10-18).
 * Aynı adlı bir düğün bu sayfaların arkasında kalıp erişilemez olurdu.
 */
export const RESERVED_SLUGS = [
  "api",
  "cdn",
  "demo",
  "privacy",
  "terms",
  "print",
  "icons",
  "assets",
  // Platformun demo düğünü (F7) — bir organizatör bu slug'ı alırsa demo kurulumu çakışır.
  "sample-wedding",
  // Online satın alma sayfası (F8b).
  "checkout",
  // Organizatör kayıt sayfası (organizer app `/signup`, 2026-10-15).
  "signup",
  // Organizatör "Paketlerim" sayfası (organizer app `/packages`, 2026-10-16).
  "packages",
] as const;

/**
 * Platformun canlı demo düğünü (admin "Satış & Site → Demo"dan kurulur).
 * Telefonsuz demo girişi YALNIZ bu slug + `config.demo === true` + hiç
 * organizatörü olmayan düğünde çalışır (bkz. worker lib/demo.ts).
 */
export const DEMO_SLUG = "sample-wedding";
/**
 * Demo düğününün 2026-10-18 öncesi slug'ı — YALNIZ veriyi yeni adrese taşımak için (worker
 * `findDemoWedding` satırı bir kez yeniden adlandırır). Eski link yönlendirilmez (kullanıcı kararı).
 */
export const LEGACY_DEMO_SLUG = "ornek-dugun";

export const slugSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, "Lowercase alphanumeric and hyphens only")
  .refine((s) => !(RESERVED_SLUGS as readonly string[]).includes(s), "This address is reserved");

export const languageConfigSchema = z.object({
  language: z.enum(["tr", "en", "fr", "es"]),
});

export const weddingConfigSchema = z.object({
  language: z.enum(["tr", "en", "fr", "es"]).optional(),
  theme: z.object({
    primaryColor: z.string().optional(),
    coverImage: z.string().optional(),
  }).optional(),
  join: z.object({
    preset: z.enum(["romantic", "modern", "minimal", "boho", "dark-romance"]).optional(),
    palette: z.string().max(50).optional(),
    fontPair: z.string().max(50).optional(),
  }).optional(),
  rsvp: z.object({
    showDietField: z.boolean().optional(),
    showNotesField: z.boolean().optional(),
    maxHeadcount: z.number().int().min(1).max(20).optional(),
  }).optional(),
  features: z.object({
    gallery: z.boolean().optional(),
    slideshow: z.boolean().optional(),
    upload: z.boolean().optional(),
  }).optional(),
  registration: z.object({
    allowSelfRegister: z.boolean().optional(),
    requireOtp: z.boolean().optional(),
    registrationOpensAt: z.string().datetime({ offset: true }).optional().nullable(),
    registrationClosesAt: z.string().datetime({ offset: true }).optional().nullable(),
  }).optional(),
}).default({});

/**
 * Opsiyonel ikinci etkinlik (kına / nikah / after-party). Ana `dateTime`'dan
 * ÖNCE de SONRA da olabilir — hiçbir yerde sıra varsayımı yapma.
 */
export const event2LabelSchema = z.enum(["kina", "nikah", "dugun", "after_party", "other"]);

/**
 * İkinci etkinliğin alanları birbirine bağlı: tarih varsa tür zorunlu, tür
 * "other" ise serbest metin zorunlu, "other" değilse serbest metin yasak.
 * ⚠️ Ayrı fonksiyon çünkü `.superRefine()` `ZodEffects` döndürür ve `ZodEffects`
 * üzerinde `.partial()/.omit()` çağrılamaz — create ve update şemalarının ikisi
 * de temel objeden türeyip bu refine'ı BAĞIMSIZ olarak uygular.
 */
type Event2LabelValue = z.infer<typeof event2LabelSchema>;

/** Etiket ↔ serbest metin tutarlılığı — iki etkinlik için de aynı kural. */
function checkLabelPair(
  ctx: z.RefinementCtx,
  labelPath: string,
  customPath: string,
  label: Event2LabelValue | null | undefined,
  custom: string | null | undefined,
) {
  if (label === "other" && !custom?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [customPath],
      message: "Custom label required when label is 'other'",
    });
  }
  if (label && label !== "other" && custom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [customPath],
      message: "Custom label only allowed when label is 'other'",
    });
  }
  if (!label && custom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [labelPath],
      message: "Custom label requires a label",
    });
  }
}

function validateEvent2(
  val: {
    event1Label?: Event2LabelValue | null;
    event1LabelCustom?: string | null;
    event2DateTime?: string | null;
    event2Label?: Event2LabelValue | null;
    event2LabelCustom?: string | null;
  },
  ctx: z.RefinementCtx,
) {
  // Ana etkinliğin etiketi OPSİYONEL (null = eski davranış "Düğün").
  checkLabelPair(ctx, "event1Label", "event1LabelCustom", val.event1Label, val.event1LabelCustom);

  // İkinci etkinlikte tarih varsa etiket ZORUNLU — adsız bir etkinlik
  // programda "?" olarak görünürdü.
  if (val.event2DateTime && !val.event2Label) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["event2Label"],
      message: "Second event requires a label",
    });
  }
  checkLabelPair(ctx, "event2Label", "event2LabelCustom", val.event2Label, val.event2LabelCustom);
}

const weddingBaseSchema = z.object({
  slug: slugSchema,
  title: z.string().min(1).max(200),
  dateTime: z.string().min(1),
  location: z.string().min(1).max(500),
  description: z.string().max(2000).optional(),
  config: weddingConfigSchema.optional(),
  // Ana etkinliğin türü — `null` = etiketsiz, UI "Düğün" yazar (geriye dönük).
  event1Label: event2LabelSchema.optional().nullable(),
  event1LabelCustom: z.string().max(80).optional().nullable(),
  // `undefined` = alana dokunma (PATCH), `null` = temizle (ikinci etkinliği kaldır).
  event2DateTime: z.string().min(1).optional().nullable(),
  event2Location: z.string().max(500).optional().nullable(),
  event2Label: event2LabelSchema.optional().nullable(),
  event2LabelCustom: z.string().max(80).optional().nullable(),
});

export const weddingCreateSchema = weddingBaseSchema.superRefine(validateEvent2);

export const weddingUpdateSchema = weddingBaseSchema
  .partial()
  .omit({ slug: true, config: true })
  .superRefine(validateEvent2);

export const organizerCreateSchema = z.object({
  email: z.string().email(),
  phoneE164: phoneSchema.optional(),
  displayName: z.string().max(100).optional(),
});

// ─── Management ───────────────────────────────────────────
export const mediaActionSchema = z.object({
  action: z.enum(["approve", "reject", "pending"]),
});

const MEDIA_ID_ACTIONS = ["goto", "hide", "unhide", "spotlight", "move"] as const;

export const slideshowControlSchema = z
  .object({
    action: z.enum(["pause", "resume", "next", "prev", "goto", "hide", "unhide", "spotlight", "move"]),
    // Director actions address media BY ID, never by index — indices shift
    // whenever the playlist reloads or items hide.
    mediaId: z.string().min(1).max(64).optional(),
    /** Target position for "move" (0-based, within the full grid order). */
    toIndex: z.number().int().min(0).max(9999).optional(),
  })
  .refine(
    (d) => !MEDIA_ID_ACTIONS.includes(d.action as (typeof MEDIA_ID_ACTIONS)[number]) || !!d.mediaId,
    { message: "mediaId is required for this action" },
  )
  .refine((d) => d.action !== "move" || d.toIndex !== undefined, {
    message: "toIndex is required for move",
  });

export const rsvpOverrideSchema = z.object({
  status: rsvpStatusSchema,
  headcount: z.number().int().min(0).max(10).optional(),
  diet: dietSchema.optional(),
});

// ─── Organizer ────────────────────────────────────────────
export const organizerProfileUpdateSchema = z.object({
  displayName: z.string().max(100).optional(),
  phoneE164: phoneSchema.optional(),
});

export const planSchema = z.string().min(1).max(50);

/**
 * Paket tanımı (admin Paketler) — paketin TÜM sayıları ve fiyatı buradan (2026-10-16 kararı:
 * "sayıların hiçbiri uydurma olmamalı"). Eski "maks. düğün" alanı yok: paket düğün başına.
 */
export const planCreateSchema = z.object({
  slug: z.string().min(1).max(50).regex(/^[a-z0-9_-]+$/),
  label: z.string().min(1).max(100),
  sortOrder: z.number().int().default(0),
  maxGuestsPerWedding: z.number().int().min(1),
  maxStorageBytes: z.number().int().min(0),
  maxMediaPerWedding: z.number().int().min(0),
  // F7: admin misconfig'e karşı üst sınırlar (SMS finansal patlamayı önler).
  maxReminderSends: z.number().int().min(0).max(10).default(1),
  // F8: SMS segment kotası — Twilio segment başına ücretlendiriyor
  // (sms_sends.segments, 040). Üst sınır 20000 (~$610): maxReminderSends(≤10)
  // × maxGuestsPerWedding(≤1000) × ~2 segment zaten bu civarda zımni tavan
  // oluşturuyordu — F7 ile aynı admin-misconfig gerekçesi.
  maxSmsSegments: z.number().int().min(0).max(20000).default(50),
  /** Satış fiyatı (₺). `null` → online satılmaz, teklifle (özel/kurumsal paket). Varsayılan paket (Free) 0. */
  priceTry: z.number().int().min(0).max(1_000_000).nullable().optional(),
  isDefault: z.boolean().default(false),
}).extend({
  // Kurumsal paketler için 5000'e kadar (SMS maliyeti ayrıca `maxSmsSegments` ile sınırlı).
  maxGuestsPerWedding: z.number().int().min(1).max(5000),
});

export const planUpdateSchema = planCreateSchema
  .partial()
  .omit({ slug: true })
  .extend({
    /** Yalnız yeniden etkinleştirme; pasife alma DELETE ile (kullanım/site kontrolleri orada). */
    isActive: z.literal(true).optional(),
  });

// ─── SMS Campaigns ────────────────────────────────────────
export const smsSegmentTypeSchema = z.enum(["all", "rsvp_none", "rsvp_no", "rsvp_maybe", "manual"]);
export const smsScheduleTypeSchema = z.enum(["now", "scheduled", "auto_chain"]);

export const smsCampaignCreateSchema = z
  .object({
    name: z.string().min(1).max(100),
    messageTemplate: z.string().min(10).max(640),
    segmentType: smsSegmentTypeSchema,
    segmentGuestIds: z.array(z.string().min(1)).min(1).max(500).optional(),
    scheduleType: smsScheduleTypeSchema,
    scheduledAt: z.string().datetime({ offset: true }).optional(),
    chainIntervalDays: z.number().int().min(1).max(30).optional(),
    chainMaxRounds: z.number().int().min(1).max(5).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.segmentType === "manual" && !val.segmentGuestIds?.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["segmentGuestIds"], message: "Manual segment requires guest ids" });
    }
    if (val.segmentType !== "manual" && val.segmentGuestIds?.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["segmentGuestIds"], message: "Guest ids only allowed for manual segment" });
    }
    if (val.scheduleType !== "now" && !val.scheduledAt) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["scheduledAt"], message: "scheduledAt required for scheduled/auto_chain" });
    }
    if (val.scheduleType === "auto_chain" && !val.chainIntervalDays) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chainIntervalDays"], message: "chainIntervalDays required for auto_chain" });
    }
    if (val.scheduleType !== "auto_chain" && (val.chainIntervalDays || val.chainMaxRounds)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["chainIntervalDays"], message: "Chain fields only allowed for auto_chain" });
    }
  });

export const smsPreviewSendSchema = z.object({
  messageTemplate: z.string().min(10).max(640),
  // Alıcı SUNUCUDA organizatörün doğrulanmış numarasından alınır — istemci
  // değeri yok sayılır (keyfi-alıcı relay'i kapatıldı). Geriye dönük uyumluluk
  // için opsiyonel bırakıldı.
  phone: phoneSchema.optional(),
});

// Organizatör telefon doğrulama (preview-send ön koşulu).
export const organizerPhoneRequestSchema = z.object({ phone: phoneSchema });
export const organizerPhoneVerifySchema = z.object({
  code: z.string().length(6).regex(/^\d{6}$/),
  verificationSid: z.string().optional(),
});

// Misafir SMS rıza durumu güncelleme (organizer UI toggle).
export const guestConsentUpdateSchema = z.object({ smsConsent: z.boolean() });
